import { z } from 'zod'

import { env } from '@/shared/config/env'

const storedAuthSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1).optional(),
  remember: z.boolean(),
})

export type StoredAuth = z.infer<typeof storedAuthSchema>

function parseStoredAuth(raw: string | null): StoredAuth | null {
  if (!raw) return null
  try {
    const result = storedAuthSchema.safeParse(JSON.parse(raw))
    return result.success ? result.data : null
  } catch {
    return null
  }
}

function migrateLegacyAuth(): StoredAuth | null {
  const accessToken = window.localStorage.getItem('auth_token')
  if (!accessToken) return null
  const refreshToken = window.localStorage.getItem('refresh_token') ?? undefined
  const migrated: StoredAuth = {
    accessToken,
    ...(refreshToken ? { refreshToken } : {}),
    remember: true,
  }
  writeStoredAuth(migrated)
  window.localStorage.removeItem('auth_token')
  window.localStorage.removeItem('refresh_token')
  return migrated
}

export function readStoredAuth(): StoredAuth | null {
  const local = parseStoredAuth(window.localStorage.getItem(env.authStorageKey))
  if (local) return local
  const session = parseStoredAuth(window.sessionStorage.getItem(env.authStorageKey))
  return session ?? migrateLegacyAuth()
}

export function writeStoredAuth(value: StoredAuth) {
  const target = value.remember ? window.localStorage : window.sessionStorage
  const other = value.remember ? window.sessionStorage : window.localStorage
  target.setItem(env.authStorageKey, JSON.stringify(value))
  other.removeItem(env.authStorageKey)
}

export function updateAccessToken(accessToken: string) {
  const stored = readStoredAuth()
  writeStoredAuth({
    accessToken,
    ...(stored?.refreshToken ? { refreshToken: stored.refreshToken } : {}),
    remember: stored?.remember ?? true,
  })
}

export function clearStoredAuth() {
  window.localStorage.removeItem(env.authStorageKey)
  window.sessionStorage.removeItem(env.authStorageKey)
  window.localStorage.removeItem('auth_token')
  window.localStorage.removeItem('refresh_token')
  window.localStorage.removeItem('auth_remember')
}
